import React from 'react'
import throttle from 'lodash/throttle'
import { bisector } from 'd3-array'
import colors from 'lib/components/charts/colors'
import {
  FONT_FAMILY,
  FONT_FAMILY_SERIF,
  FONT_LARGE,
  FONT_SMALL
} from 'lib/components/charts/constants'
import { textGap } from 'lib/text'
import { MONTHS } from 'lib/date'

const FONT = `${FONT_SMALL} ${FONT_FAMILY}`
const FONT_L = `${FONT_LARGE} ${FONT_FAMILY_SERIF}`

// Inner spacing of the tooltip
const PADDING = 15

// How far from the edge should the tooltip rest when constrained to the box
const TOOLTIP_MARGIN = 10

class LineChartTooltip extends React.PureComponent {
  static defaultProps = {
    formatY1: n => n,
    formatY2: n => n,
    label: ''
  }

  state = {
    currentX: 0,
    currentY: 0,
    isVisible: false
  }

  renderDots(index, scaleX, scaleY1, scaleY2) {
    return this.props.datasets.map(function({ key, color, points }, i) {
      let point = points[index]
      let scaleY = i < 1 ? scaleY1 : scaleY2

      return point && point.y != null ? (
        <circle
          key={key}
          cx={scaleX(point.x)}
          cy={scaleY(point.y)}
          fill={color}
          r="3.5"
        />
      ) : null
    })
  }

  renderTooltip(index) {
    const {
      datasets,
      bottom,
      height,
      width,
      left,
      right,
      formatY1,
      formatY2,
      label,
      labelY1,
      labelY2
    } = this.props

    const lineOnePoint = datasets[0].points[index]
    const lineTwoPoint = datasets[1].points[index]
    const fill = datasets[0].color
    const title = datasets[0].label

    // Determine the current date by calculating the oldest date between
    // The two points
    const date =
      lineOnePoint.x > lineTwoPoint.x ? lineOnePoint.x : lineTwoPoint.x
    const displayDate = `${MONTHS[date.getMonth()]} ${date.getFullYear()}`

    const lineOneValue =
      lineOnePoint.y == null ? 'N/A' : formatY1(lineOnePoint.y)
    const lineTwoValue =
      lineTwoPoint.y == null ? 'N/A' : formatY2(lineTwoPoint.y)

    // Group up all of the labels to determine the longest line
    // This value is used to figure out how wide the tooltip needs to be
    const textToMeasure = [
      label,
      displayDate,
      labelY1 + ' ' + formatY1(lineOnePoint.y),
      labelY2 + ' ' + formatY2(lineTwoPoint.y)
    ]

    // measure, return the width of the widest line of text in standard font
    const textWidth = textGap(textToMeasure, n => n, FONT)

    // measure, return the width of the title font in larger, serif font
    const titleWidth = textGap([title], n => n, FONT_L)

    // measure, return the largest width for the `${month} {year}` text
    const displayDateRectWidth =
      textGap(MONTHS, month => month + ' ' + date.getFullYear(), FONT) + PADDING

    // dynamically set the tooltip width based on the widest possible line of text
    // and add some padding for whitespace
    const tooltipWidth = Math.max(textWidth, titleWidth) + PADDING * 4

    // adjust manually as needed for this display
    // see simlary `y` and `height` values below that determine the tooltip layout
    const tooltipHeight = 130

    // position the tooltip horizontally to the current cursor position
    const tooltipCenter = tooltipWidth / 2
    const tooltipX = Math.max(
      left + TOOLTIP_MARGIN,
      Math.min(
        this.state.currentX - tooltipCenter,
        width - right - tooltipWidth - TOOLTIP_MARGIN
      )
    )

    // position the tooltip vertically, offset by the margin from
    // current cursor Y position // restrict tooltip edges to a minimum
    // of the margin from the chart's top and bottom grid lines
    const gridHeight = height - bottom

    const tooltipY = Math.min(
      this.state.currentY + TOOLTIP_MARGIN,
      gridHeight - tooltipHeight - TOOLTIP_MARGIN
    )

    return (
      <g
        width={tooltipWidth}
        height={tooltipHeight}
        transform={`translate(${tooltipX},${tooltipY})`}
        pointerEvents="none"
        fontFamily={FONT_FAMILY}
        fontSize={FONT_SMALL}
      >
        <rect
          x="0"
          y="0"
          width={tooltipWidth}
          height={tooltipHeight}
          shapeRendering="crispEdges"
          stroke={colors.lightGray}
          fill="white"
          opacity="0.92"
        />

        <rect
          x="0.5"
          y="0.5"
          width="6"
          height="31"
          shapeRendering="crispEdges"
          fill={fill}
        />
        <path
          d={`M0,32 L${tooltipWidth},32`}
          stroke={colors.lightGray}
          shapeRendering="crispEdges"
        />
        <text
          x={tooltipWidth / 2}
          y="18"
          fontFamily={FONT_FAMILY_SERIF}
          fontSize={FONT_LARGE}
          alignmentBaseline="middle"
          textAnchor="middle"
        >
          {title}
        </text>

        <text
          x={tooltipWidth / 2}
          y="48"
          alignmentBaseline="middle"
          textAnchor="middle"
          letterSpacing="0.1em"
        >
          {label}
        </text>

        <rect
          x={tooltipWidth / 2 - displayDateRectWidth / 2}
          y="63"
          width={displayDateRectWidth}
          height={19}
          shapeRendering="crispEdges"
          stroke={fill}
          fill="transparent"
        />
        <text
          x={tooltipWidth / 2}
          y="73"
          alignmentBaseline="middle"
          textAnchor="middle"
        >
          {`${MONTHS[date.getMonth()]} ${date.getFullYear()}`}
        </text>

        <g>
          <circle cx={PADDING} cy="98.5" r="3" fill={datasets[0].color} />
          <text y="102" alignmentBaseline="middle" textAnchor="end">
            <tspan x={PADDING * 2} textAnchor="start">
              {labelY1}
            </tspan>
            <tspan x={tooltipWidth - PADDING} textAnchor="end">
              {lineOneValue}
            </tspan>
          </text>
        </g>

        <g>
          <circle cx={PADDING} cy="115.5" r="3" fill={datasets[1].color} />
          <text y="119" alignmentBaseline="middle" textAnchor="end">
            <tspan x={PADDING * 2} textAnchor="start">
              {labelY2}
            </tspan>
            <tspan x={tooltipWidth - PADDING} textAnchor="end">
              {lineTwoValue}
            </tspan>
          </text>
        </g>
      </g>
    )
  }

  show = () => {
    this.setState({ isVisible: true })
  }

  hide = () => {
    this.setState({ isVisible: false })
  }

  updatePosition = e => {
    this.setCurrentPosition(e.nativeEvent.offsetX, e.nativeEvent.offsetY)
  }

  setCurrentPosition = throttle((currentX, currentY) => {
    this.setState({ currentX, currentY })
  }, 25)

  render() {
    const {
      datasets,
      scaleX,
      scaleY1,
      scaleY2,
      bottom,
      left,
      right,
      top,
      height,
      width
    } = this.props
    const [yTop, yBottom] = scaleY1.range()
    const x0 = scaleX.invert(this.state.currentX) // date representation of current mouse position

    let closestIndex = -1
    let activeX = x0

    if (datasets.length) {
      const data = datasets
        .slice()
        .sort(dataset => -dataset.points.length)
        .pop()

      if (data.points.length) {
        const bisectDate = bisector(d => d.x).left
        const index = bisectDate(data.points, x0, 1)

        closestIndex = Math.max(0, Math.min(index, data.points.length - 1))

        activeX = data.points[closestIndex].x
      }
    }

    const shouldShow = closestIndex > -1 && this.state.isVisible

    return (
      <g>
        {shouldShow && (
          <path
            d={`M${scaleX(activeX)},${yTop} L${scaleX(activeX)},${yBottom}`}
            stroke={colors.lightGray}
          />
        )}

        {shouldShow && this.renderDots(closestIndex, scaleX, scaleY1, scaleY2)}

        {shouldShow && this.renderTooltip(closestIndex)}

        <rect
          fill="transparent"
          height={height - top - bottom}
          width={width - left - right}
          x={left}
          y={top}
          onClick={this.updatePosition}
          onMouseMove={this.updatePosition}
          onMouseEnter={this.show}
          onMouseLeave={this.hide}
        />
      </g>
    )
  }
}

export default LineChartTooltip



// WEBPACK FOOTER //
// /home/runner/work/web-interactive/web-interactive/lib/components/charts/line-chart-tooltip.js